/**
 * Asset action hooks for document state management
 *
 * These hooks call direct named methods on the Zustand store (idiomatic Zustand pattern),
 * replacing the previous dispatch(actionCreator(...)) indirection.
 *
 * The store methods handle undo/redo, action records, and subscriber notifications internally.
 */
import type {IAssetsMap} from "@buildwithflux/core";
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";
import type {DocumentStore} from "modules/document/state/document";

/**
 * Hook to get the setAssets action
 *
 * Calls the direct setAssets method on the Zustand store.
 *
 * @returns A function to set/update assets
 */
export function useSetAssets(): (assets: IAssetsMap) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (assets: IAssetsMap) => {
            documentStore.getState().setAssets(assets);
        },
        [documentStore],
    );
}

/**
 * Hook to get the removeAssets action
 *
 * Calls the direct removeAssets method on the Zustand store.
 *
 * @returns A function to remove assets by their UIDs
 */
export function useRemoveAssets(): (assetUids: string[]) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (assetUids: string[]) => {
            documentStore.getState().removeAssets(assetUids);
        },
        [documentStore],
    );
}

/**
 * Factory function to create asset actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to perform asset actions.
 *
 * @param documentStore - The DocumentStore from FluxServices
 * @returns An object with asset action methods
 */
export function createAssetActions(documentStore: DocumentStore) {
    return {
        setAssets: (assets: IAssetsMap) => {
            documentStore.getState().setAssets(assets);
        },
        removeAssets: (assetUids: string[]) => {
            documentStore.getState().removeAssets(assetUids);
        },
    };
}
